import {
    type DateFormat,
    DEFAULT_USER_PREFERENCES,
    dateFormatSchema,
    type TimeFormat,
    timeFormatSchema,
    type UserPreferences,
    type WeekStart,
    weekStartSchema,
} from "@orsane/schema/users";
import { format } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { openCookieSettings } from "@/lib/cookieConsent";
import { useUpdateUserPreferences } from "@/mutations/user-mutations";

// Today at 15:45: the date examples read as today, and an afternoon time shows the
// 12h/24h difference.
const EXAMPLE_DATE = new Date();
EXAMPLE_DATE.setHours(15, 45, 0, 0);

const TIME_FORMAT_LABELS: Record<TimeFormat, string> = {
    "12h": `12-hour (${format(EXAMPLE_DATE, "h:mm a")})`,
    "24h": `24-hour (${format(EXAMPLE_DATE, "HH:mm")})`,
};

const WEEK_START_LABELS: Record<WeekStart, string> = {
    sunday: "Sunday",
    monday: "Monday",
    saturday: "Saturday",
};

const dateFormatLabel = (dateFormat: DateFormat) => format(EXAMPLE_DATE, dateFormat);

function withDefaultMarker<K extends keyof UserPreferences>(key: K, value: UserPreferences[K], label: string) {
    return DEFAULT_USER_PREFERENCES[key] === value ? `${label} (default)` : label;
}

function PreferenceRow({
    label,
    description,
    children,
}: {
    label: string;
    description: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex items-center justify-between gap-6 py-3 not-last:border-b">
            <div className="min-w-0">
                <div className="text-base font-medium">{label}</div>
                <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            <div className="w-56 shrink-0">{children}</div>
        </div>
    );
}

function PreferenceSelect<K extends keyof UserPreferences>({
    name,
    value,
    options,
    labelFor,
    onChange,
}: {
    name: K;
    value: UserPreferences[K];
    options: readonly UserPreferences[K][];
    labelFor: (value: UserPreferences[K]) => string;
    onChange: (value: UserPreferences[K]) => void;
}) {
    return (
        <Select
            value={value}
            onValueChange={(next) => {
                const option = options.find((candidate) => candidate === next);
                if (option !== undefined && option !== value) onChange(option);
            }}
        >
            <SelectTrigger className="w-full" size="sm" aria-label={name}>
                <SelectValue>
                    {(current: UserPreferences[K]) => withDefaultMarker(name, current, labelFor(current))}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectGroup>
                    {options.map((option) => (
                        <SelectItem key={option} value={option}>
                            {withDefaultMarker(name, option, labelFor(option))}
                        </SelectItem>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    );
}

export default function PreferencesSettings() {
    const preferences = useUserPreferences();
    const { mutate } = useUpdateUserPreferences();

    const update = (patch: Partial<UserPreferences>) => {
        mutate(patch, {
            onSuccess: () => toast.success("Preferences updated"),
            onError: () => toast.error("Could not update your preferences"),
        });
    };

    return (
        <div className="flex flex-col">
            <PreferenceRow label="Date notation" description="How dates are written across Orsane.">
                <PreferenceSelect
                    name="dateFormat"
                    value={preferences.dateFormat}
                    options={dateFormatSchema.options}
                    labelFor={dateFormatLabel}
                    onChange={(dateFormat) => update({ dateFormat })}
                />
            </PreferenceRow>
            <PreferenceRow label="Time notation" description="Show times on a 12-hour or a 24-hour clock.">
                <PreferenceSelect
                    name="timeFormat"
                    value={preferences.timeFormat}
                    options={timeFormatSchema.options}
                    labelFor={(timeFormat) => TIME_FORMAT_LABELS[timeFormat]}
                    onChange={(timeFormat) => update({ timeFormat })}
                />
            </PreferenceRow>
            <PreferenceRow
                label="Start of the week"
                description="The first day in date pickers, and where 'This week' and 'Next week' begin."
            >
                <PreferenceSelect
                    name="weekStart"
                    value={preferences.weekStart}
                    options={weekStartSchema.options}
                    labelFor={(weekStart) => WEEK_START_LABELS[weekStart]}
                    onChange={(weekStart) => update({ weekStart })}
                />
            </PreferenceRow>
            <div className="flex items-center justify-between gap-6 py-3">
                <div className="min-w-0">
                    <div className="text-base font-medium">Cookies</div>
                    <p className="text-sm text-muted-foreground">
                        Essential cookies stay on. Statistics and marketing stay off until you allow them. The same
                        choice is used on www.orsane.app.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-56 shrink-0"
                    data-cc="show-preferencesModal"
                    onClick={() => openCookieSettings()}
                >
                    Cookie settings
                </Button>
            </div>
        </div>
    );
}
